/**
 * Converte valor numérico (reais) para extenso em português (PT-BR).
 * Entrada pode ser string no formato BR ("1.180,88") ou número.
 */

const UNIDADES = [
  '',
  'um',
  'dois',
  'três',
  'quatro',
  'cinco',
  'seis',
  'sete',
  'oito',
  'nove',
];

const DEZENAS_ESPECIAIS = [
  'dez',
  'onze',
  'doze',
  'treze',
  'catorze',
  'quinze',
  'dezesseis',
  'dezessete',
  'dezoito',
  'dezenove',
];

const DEZENAS = [
  '',
  '',
  'vinte',
  'trinta',
  'quarenta',
  'cinquenta',
  'sessenta',
  'setenta',
  'oitenta',
  'noventa',
];

const CENTENAS = [
  '',
  'cento',
  'duzentos',
  'trezentos',
  'quatrocentos',
  'quinhentos',
  'seiscentos',
  'setecentos',
  'oitocentos',
  'novecentos',
];

function escreverCentena(n: number): string {
  if (n === 0) return '';
  if (n === 100) return 'cem';
  const c = Math.floor(n / 100);
  const resto = n % 100;
  const parteCentena = CENTENAS[c];
  if (resto === 0) return parteCentena;
  return `${parteCentena} e ${escreverDezena(resto)}`;
}

function escreverDezena(n: number): string {
  if (n < 10) return UNIDADES[n];
  if (n < 20) return DEZENAS_ESPECIAIS[n - 10];
  const d = Math.floor(n / 10);
  const u = n % 10;
  if (u === 0) return DEZENAS[d];
  return `${DEZENAS[d]} e ${UNIDADES[u]}`;
}

function escreverGrupo(n: number, singular: string, plural: string): string {
  if (n === 0) return '';
  const num = n;
  if (num === 1) return `${escreverCentena(num)} ${singular}`;
  const texto = escreverCentena(num);
  return `${texto} ${plural}`;
}

/**
 * Converte um número (0-999) para extenso.
 */
function numeroAte999ParaExtenso(n: number): string {
  if (n === 0) return 'zero';
  return escreverCentena(n).trim();
}

/**
 * Converte valor em reais para extenso (ex: 1180.88 -> "um mil cento e oitenta reais e oitenta e oito centavos").
 * @param valor - Número ou string no formato BR (ex: "1.180,88")
 */
export function valorPorExtenso(valor: number | string): string {
  let num = 0;
  if (typeof valor === 'string') {
    const limpo = valor.replace(/\D/g, '');
    if (limpo.length <= 2) {
      num = Number(limpo) / 100;
    } else {
      num = Number(limpo.slice(0, -2) + '.' + limpo.slice(-2));
    }
  } else {
    num = valor;
  }

  const inteiro = Math.floor(num);
  const centavos = Math.round((num - inteiro) * 100);

  const partes: string[] = [];

  if (inteiro === 0 && centavos === 0) {
    return 'zero reais';
  }

  if (inteiro >= 1_000_000) {
    const milhao = Math.floor(inteiro / 1_000_000);
    const resto = inteiro % 1_000_000;
    partes.push(
      milhao === 1
        ? 'um milhão'
        : `${numeroAte999ParaExtenso(milhao)} milhões`
    );
    if (resto > 0) {
      partes.push(escreverGrupo(resto, 'real', 'reais'));
    }
  } else if (inteiro >= 1_000) {
    const mil = Math.floor(inteiro / 1_000);
    const resto = inteiro % 1_000;
    partes.push(
      mil === 1 ? 'um mil' : `${numeroAte999ParaExtenso(mil)} mil`
    );
    if (resto > 0) {
      partes.push(escreverGrupo(resto, 'real', 'reais'));
    }
  } else if (inteiro > 0) {
    partes.push(escreverGrupo(inteiro, 'real', 'reais'));
  }

  const reaisTexto = partes.join(' e ').trim();

  if (centavos > 0) {
    const centavosTexto =
      centavos === 1
        ? 'um centavo'
        : `${numeroAte999ParaExtenso(centavos)} centavos`;
    if (reaisTexto) {
      return `${reaisTexto} e ${centavosTexto}`;
    }
    return centavosTexto;
  }

  return reaisTexto;
}
